import React from 'react';
import { BrowserRouter, Routes, Route, Outlet, useLocation } from 'react-router-dom';
import './App.css';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { WishlistProvider } from './context/WishlistContext';
import Header from './components/Header';
import Footer from './components/Footer';
import CartDrawer from './components/CartDrawer';
import ViewCartBar from './components/ViewCartBar';
import IntroOverlay from './components/IntroOverlay';
import PromoStrip from './components/PromoStrip';
import { Toaster } from './components/ui/toaster';
import { initAnalytics, trackPageView } from './lib/analytics';
import HomePage from './pages/HomePage';

const lazyPages = {
  ShopPage: React.lazy(() => import('./pages/ShopPage')),
  ProductDetailPage: React.lazy(() => import('./pages/ProductDetailPage')),
  CartPage: React.lazy(() => import('./pages/CartPage')),
  CheckoutPage: React.lazy(() => import('./pages/CheckoutPage')),
  LoginPage: React.lazy(() => import('./pages/LoginPage')),
  ForgotPasswordPage: React.lazy(() => import('./pages/ForgotPasswordPage')),
  ResetPasswordPage: React.lazy(() => import('./pages/ResetPasswordPage')),
  AccountPage: React.lazy(() => import('./pages/AccountPage')),
  AddressesPage: React.lazy(() => import('./pages/AddressesPage')),
  AboutPage: React.lazy(() => import('./pages/AboutPage')),
  ContactPage: React.lazy(() => import('./pages/ContactPage')),
  BlogListPage: React.lazy(() => import('./pages/BlogListPage')),
  BlogDetailPage: React.lazy(() => import('./pages/BlogDetailPage')),
  WishlistPage: React.lazy(() => import('./pages/WishlistPage')),
  ComingSoonPage: React.lazy(() => import('./pages/ComingSoonPage')),
  NotFoundPage: React.lazy(() => import('./pages/NotFoundPage')),
  LegalPage: React.lazy(() => import('./pages/LegalPage')),
  AuthCallback: React.lazy(() => import('./pages/AuthCallback')),
  InstitutionSalesPage: React.lazy(() => import('./pages/InstitutionSalesPage')),
  B2BPage: React.lazy(() => import('./pages/B2BPage')),
  DiscoverPage: React.lazy(() => import('./pages/DiscoverPage')),
  BundleBuilderPage: React.lazy(() => import('./pages/BundleBuilderPage')),
  RecipePage: React.lazy(() => import('./pages/RecipePage')),
  OrderConfirmationPage: React.lazy(() => import('./pages/OrderConfirmationPage')),
  CompanyRegistrationsPage: React.lazy(() => import('./pages/CompanyRegistrationsPage')),
  AdminApp: React.lazy(() => import('./pages/AdminApp')),
};
const ShopPage = lazyPages.ShopPage;
const ProductDetailPage = lazyPages.ProductDetailPage;
const CartPage = lazyPages.CartPage;
const CheckoutPage = lazyPages.CheckoutPage;
const LoginPage = lazyPages.LoginPage;
const ForgotPasswordPage = lazyPages.ForgotPasswordPage;
const ResetPasswordPage = lazyPages.ResetPasswordPage;
const AccountPage = lazyPages.AccountPage;
const AddressesPage = lazyPages.AddressesPage;
const AboutPage = lazyPages.AboutPage;
const ContactPage = lazyPages.ContactPage;
const BlogListPage = lazyPages.BlogListPage;
const BlogDetailPage = lazyPages.BlogDetailPage;
const WishlistPage = lazyPages.WishlistPage;
const ComingSoonPage = lazyPages.ComingSoonPage;
const NotFoundPage = lazyPages.NotFoundPage;
const LegalPage = lazyPages.LegalPage;
const AuthCallback = lazyPages.AuthCallback;
const InstitutionSalesPage = lazyPages.InstitutionSalesPage;
const B2BPage = lazyPages.B2BPage;
const DiscoverPage = lazyPages.DiscoverPage;
const BundleBuilderPage = lazyPages.BundleBuilderPage;
const RecipePage = lazyPages.RecipePage;
const OrderConfirmationPage = lazyPages.OrderConfirmationPage;
const CompanyRegistrationsPage = lazyPages.CompanyRegistrationsPage;
const AdminApp = lazyPages.AdminApp;

function SkipLink() {
  return (
    <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[9999] focus:bg-brand-gold focus:text-brand-dark focus:px-4 focus:py-2 focus:rounded-md focus:font-semibold">
      Skip to content
    </a>
  );
}

function RouteFallback() {
  return <div className="min-h-[60vh] flex items-center justify-center text-brand-green/60 text-sm" data-testid="route-loading">Loading…</div>;
}

function AnimatedOutlet() {
  const { pathname } = useLocation();
  return (
    <div key={pathname} className="route-fade">
      <React.Suspense fallback={<RouteFallback />}>
        <Outlet />
      </React.Suspense>
    </div>
  );
}

function SiteLayout() {
  return (
    <div>
      <SkipLink />
      <PromoStrip code="RARE10" percent={10} />
      <Header />
      <CartDrawer />
      <main id="main-content" tabIndex={-1} className="min-h-[60vh] focus:outline-none pb-24 lg:pb-24">
        <AnimatedOutlet />
      </main>
      <Footer />
      <ViewCartBar />
    </div>
  );
}

function ScrollTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    trackPageView(pathname);
  }, [pathname]);
  return null;
}

// Root error boundary — without it, any uncaught render error unmounts the
// whole React tree, which empties #root and leaves the pre-hydration SEO
// shell ("Loading the full experience…") visible forever. This gives the
// customer a graceful recovery instead of a dead page.
class RootErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }
  static getDerivedStateFromError(err) {
    return { hasError: true, message: err?.message || 'Unexpected error' };
  }
  componentDidCatch(err, info) {
    console.error('Storefront crash:', err, info?.componentStack);
    // Never let an error screen get indexed or used as a snippet.
    let m = document.head.querySelector('meta[name="robots"]');
    if (!m) { m = document.createElement('meta'); m.setAttribute('name', 'robots'); document.head.appendChild(m); }
    m.setAttribute('content', 'noindex, nofollow');
    document.title = 'Rare Locals';
  }
  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#fbf8f2] text-center px-6" data-testid="app-error-boundary">
        <h1 className="font-serif-brand text-3xl text-[#0d2818] mb-3">Something went wrong</h1>
        <p className="text-sm text-[#26543f] max-w-sm">A small hiccup on our side. Your cart is saved locally — a refresh usually fixes it.</p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => window.location.reload()}
            className="h-11 px-6 rounded-full bg-[#0d2818] text-[#c9a24b] text-sm font-semibold hover:bg-[#1a3d2e] transition-colors"
            data-testid="error-reload-btn"
          >
            Refresh page
          </button>
          <button
            onClick={() => { try { localStorage.removeItem('rl_cart'); } catch (_) {} window.location.href = '/'; }}
            className="h-11 px-6 rounded-full border border-[#c9a24b]/40 text-[#0d2818] text-sm hover:bg-white transition-colors"
            data-testid="error-reset-btn"
          >
            Clear cart &amp; go home
          </button>
        </div>
      </div>
    );
  }
}

export default function App() {
  React.useEffect(() => { initAnalytics(); }, []);
  return (
    <div className="App">
      <RootErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <CartProvider>
              <WishlistProvider>
                <IntroOverlay />
                <ScrollTop />
                <Routes>
                <Route path="/admin/*" element={<React.Suspense fallback={<RouteFallback />}><AdminApp /></React.Suspense>} />
                <Route element={<SiteLayout />}>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/shop" element={<ShopPage />} />
                  <Route path="/shop/:slug" element={<ShopPage />} />
                  <Route path="/product/:slug" element={<ProductDetailPage />} />
                  <Route path="/cart" element={<CartPage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/account" element={<AccountPage />} />
                  <Route path="/account/addresses" element={<AddressesPage />} />
                  <Route path="/wishlist" element={<WishlistPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/blog" element={<BlogListPage />} />
                  <Route path="/blog/:slug" element={<BlogDetailPage />} />
                  <Route path="/b2b" element={<B2BPage />} />
                  <Route path="/b2b/:slug" element={<B2BPage />} />
                  <Route path="/institution-sales" element={<InstitutionSalesPage />} />
                  <Route path="/institution-sales/:slug" element={<InstitutionSalesPage />} />
                  <Route path="/order/:id" element={<OrderConfirmationPage />} />
                  <Route path="/legal/:slug" element={<LegalPage />} />
                  <Route path="/company-registrations" element={<CompanyRegistrationsPage />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Route>
              </Routes>
              <Toaster />
            </WishlistProvider>
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
      </RootErrorBoundary>
    </div>
  );
}
